import { useState } from 'react';

import type { ProcessStepItem } from '@/components/ui';

// 권한 부여/변경/회수의 공통 반영 과정 — PermTab(프로젝트 권한 탭)과
// MemberPerms(멤버별 권한)가 함께 사용.
//
// 단계: 권한 반영(access_grants · Redash · BigQuery ACL) → BI 동기화 예약 → 목록 갱신.
// - grantCalls: access_grants/BQ ACL을 반영하는 호출들(순차 실행). 권한 자체는 여기서 즉시 반영된다.
// - Partners(BI) 동기화는 백엔드에서 디바운스로 처리된다. 여러 명을 연달아 수정하면
//   마지막 변경 기준 3분 뒤 1회만 실행되므로, 프론트는 완료를 기다리지 않고 '예약'만 표시하고 끝낸다.
//   (예전에는 awaitPartnersSync로 완료까지 polling 대기했으나, idle 3분에서는 UX가 나빠 제거)
// - onReload: 권한 반영 후 호출측 목록을 다시 불러오는 콜백(멤버 목록/유저 grant 등).

// 스텝퍼 단계 사이 시각적 텀 — 진행이 너무 빨라 단계가 안 보이는 것을 방지.
const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export type GrantCall = () => Promise<{ sync_job_id?: string | null; bq_sync_status?: string | null; bq_sync_error?: string | null }>;

export interface GrantProcessControl {
  processSteps: ProcessStepItem[] | null;
  processTitle: string;
  // 권한 반영에 성공하면 true. 실패 시 grantCalls가 throw → 호출측 catch에서 처리(closeStepper).
  runGrantProcess: (title: string, grantCalls: GrantCall[], onReload: () => void) => Promise<boolean>;
  closeStepper: () => void;
}

export function useGrantProcess(): GrantProcessControl {
  const [processSteps, setProcessSteps] = useState<ProcessStepItem[] | null>(null);
  const [processTitle, setProcessTitle] = useState('');

  const runGrantProcess = async (
    title: string,
    grantCalls: GrantCall[],
    onReload: () => void,
  ): Promise<boolean> => {
    setProcessTitle(title);
    setProcessSteps([
      { key: 'apply', label: '권한 반영', desc: 'access_grants · Redash · BigQuery ACL', status: 'active' },
      { key: 'partners', label: 'BI 동기화 예약', desc: '마지막 변경 3분 뒤 자동 반영', status: 'pending' },
      { key: 'reload', label: '목록 갱신', desc: '최신 권한 다시 불러오기', status: 'pending' },
    ]);

    // 1) 권한 반영 — grant API 순차 실행. 권한(access_grants·BQ ACL)은 여기서 즉시 반영된다.
    //    각 호출이 partners refresh를 trigger하지만, 백엔드 디바운스가 마지막 호출 기준으로 재예약한다.
    for (const call of grantCalls) {
      const result = await call();
      // HTTP 200에도 실제 ACL 반영 실패가 포함될 수 있다. 다음 변경을 진행하지 않는다.
      if (result.bq_sync_status === 'failed' || result.bq_sync_status === 'partial') {
        throw new Error(result.bq_sync_error || '일부 권한 반영에 실패했습니다. 현재 권한을 다시 확인해 주세요.');
      }
    }
    // Partners(BI) 동기화는 백엔드 디바운스가 알아서 3분 뒤 1회 실행 — 대기하지 않고 '예약' 완료로 표시.
    setProcessSteps((prev) =>
      prev
        ? prev.map((s) =>
            s.key === 'apply' || s.key === 'partners' ? { ...s, status: 'done' } : s,
          )
        : prev,
    );
    await delay(300);

    // 2) 목록 갱신.
    setProcessSteps((prev) =>
      prev ? prev.map((s) => (s.key === 'reload' ? { ...s, status: 'active' } : s)) : prev,
    );
    onReload();
    await delay(300);
    setProcessSteps((prev) =>
      prev ? prev.map((s) => (s.key === 'reload' ? { ...s, status: 'done' } : s)) : prev,
    );
    return true;
  };

  const closeStepper = () => setProcessSteps(null);

  return { processSteps, processTitle, runGrantProcess, closeStepper };
}
